import { useCallback, useState } from 'react';
import type { Company, CompanyDraft } from '@domain/entities';
import { ValidationError } from '@domain/errors';
import type { CompanyCreateInput } from '@domain/repositories';
import type { ModerationStatus } from '@domain/value-objects';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAsync, useDebounced, useSelection } from '../hooks/use-async';
import { useRealtimeVersion } from '../providers/realtime-context';
import { useIsMobile } from '../hooks/use-is-mobile';
import {
  BulkBar,
  Checkbox,
  ConfirmModal,
  Drawer,
  EmptyState,
  ErrorState,
  FilterPills,
  Modal,
  Pager,
  RefreshButton,
  SearchInput,
  StatusBadge,
  TableSkeleton,
  TextField,
} from '../components/ui';
import { PAGE_SIZE } from '../theme/tokens';

type StatusFilter = ModerationStatus | 'all';

const FILTERS = [
  { value: 'all' as const, label: 'Tous' },
  { value: 'pending' as const, label: 'En attente' },
  { value: 'approved' as const, label: 'Approuvé' },
  { value: 'rejected' as const, label: 'Rejeté' },
];

const CHANNEL_LABELS: Record<string, string> = {
  call: 'Appel',
  whatsapp: 'WhatsApp',
  email: 'Email',
};

const EMPTY_DRAFT: CompanyDraft = {
  name: '',
  type: '',
  city: '',
  phone: null,
  contactChannel: 'call',
  contactDestination: '',
};

const EMPTY_CREATE: CompanyCreateInput = {
  ownerId: '',
  name: '',
  type: '',
  city: '',
  contactChannel: 'call',
  contactDestination: '',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return iso.startsWith('20') ? iso.slice(0, 10) : iso;
}

export function CompaniesPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const vBusinesses = useRealtimeVersion('businesses');

  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search);

  const [detail, setDetail] = useState<Company | null>(null);
  const [editing, setEditing] = useState<Company | null>(null);
  const [draft, setDraft] = useState<CompanyDraft>(EMPTY_DRAFT);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState<CompanyCreateInput>(EMPTY_CREATE);
  const [createSaving, setCreateSaving] = useState(false);
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});

  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const isMobile = useIsMobile();
  const selection = useSelection();

  const resource = useAsync(
    () =>
      container.companies.search.execute({
        status,
        search: debouncedSearch,
        page,
        pageSize: PAGE_SIZE,
      }),
    [container, status, debouncedSearch, page, vBusinesses],
  );

  const typesResource = useAsync(() => container.businessTypes.list.execute(), [container]);
  const citiesResource = useAsync(() => container.cities.list.execute(), [container]);
  const usersResource = useAsync(
    () => container.users.search.execute({ status: 'all', search: '', page: 1, pageSize: 100 }),
    [container],
  );

  const rows = resource.data?.items ?? [];
  const visibleIds = rows.map((row) => row.id);
  const typeOptions = (typesResource.data ?? []).filter((t) => t.isActive);
  const cityOptions = (citiesResource.data ?? []).filter((c) => c.isActive);
  const userOptions = usersResource.data?.items ?? [];

  const moderate = useCallback(
    async (ids: readonly string[], next: 'approved' | 'rejected') => {
      try {
        await container.companies.moderate.execute({ ids, status: next });
        toast.notify(
          `${ids.length} compte(s) ${next === 'approved' ? 'approuvé(s)' : 'rejeté(s)'}`,
        );
        selection.clear();
        setDetail(null);
        resource.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, selection, resource],
  );

  const openEditor = useCallback((company: Company) => {
    setDetail(null);
    setEditing(company);
    setFieldErrors({});
    setDraft({
      name: company.name,
      type: company.type,
      city: company.city,
      phone: company.phone,
      contactChannel: company.contactChannel,
      contactDestination: company.contactDestination,
    });
  }, []);

  const save = useCallback(async () => {
    if (!editing) return;
    setSaving(true);
    setFieldErrors({});
    try {
      await container.companies.update.execute({ id: editing.id, draft });
      toast.notify('Entreprise mise à jour');
      setEditing(null);
      resource.reload();
    } catch (error) {
      if (error instanceof ValidationError) setFieldErrors({ ...error.fields });
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editing, draft, toast, resource]);

  const deleteCompany = useCallback(async (id: string) => {
    try {
      await container.companies.remove.execute(id);
      toast.notify('Entreprise supprimée');
      setDetail(null);
      resource.reload();
    } catch (error) {
      toast.notifyError(error);
    }
  }, [container, toast, resource]);

  const createCompany = useCallback(async () => {
    setCreateSaving(true);
    setCreateErrors({});
    try {
      await container.companies.create.execute(createDraft);
      toast.notify('Entreprise créée');
      setCreating(false);
      setCreateDraft(EMPTY_CREATE);
      resource.reload();
    } catch (error) {
      if (error instanceof ValidationError) setCreateErrors({ ...error.fields });
      toast.notifyError(error);
    } finally {
      setCreateSaving(false);
    }
  }, [container, createDraft, toast, resource]);

  return (
    <>
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <FilterPills
            options={FILTERS}
            value={status}
            onChange={(next) => {
              setStatus(next);
              setPage(1);
            }}
          />
          <SearchInput
            value={search}
            placeholder="Rechercher une entreprise…"
            onChange={(next) => {
              setSearch(next);
              setPage(1);
            }}
          />
        </div>
        <div className="bbm-toolbar__end">
          <button
            type="button"
            className="bbm-btn bbm-btn--primary"
            onClick={() => {
              setCreateDraft(EMPTY_CREATE);
              setCreateErrors({});
              setCreating(true);
            }}
          >
            + Nouvelle entreprise
          </button>
          <RefreshButton onRefresh={resource.reload} loading={resource.loading} />
        </div>
      </div>

      {resource.loading ? <TableSkeleton /> : null}

      {resource.error ? (
        <ErrorState
          status={resource.error.status}
          message={resource.error.message}
          onRetry={resource.reload}
        />
      ) : null}

      {!resource.loading && !resource.error && rows.length === 0 ? (
        <EmptyState
          title="Aucun résultat"
          body="Aucune entreprise ne correspond à ces filtres."
          actionLabel="Réinitialiser les filtres"
          onAction={() => {
            setStatus('all');
            setSearch('');
            setPage(1);
          }}
        />
      ) : null}

      {!resource.loading && !resource.error && rows.length > 0 && resource.data ? (
        <>
          <BulkBar count={selection.selected.length} onClear={selection.clear}>
            <button
              type="button"
              className="bbm-btn bbm-btn--success"
              onClick={() => setPending({
                title: 'Approuver la sélection ?',
                body: 'Les entreprises sélectionnées seront approuvées.',
                confirmLabel: 'Approuver',
                fn: () => void moderate(selection.selected, 'approved'),
              })}
            >
              Approuver la sélection
            </button>
            <button
              type="button"
              className="bbm-bulkbar__ghost"
              onClick={() => setPending({
                title: 'Rejeter la sélection ?',
                body: 'Les entreprises sélectionnées seront rejetées.',
                confirmLabel: 'Rejeter',
                danger: true,
                fn: () => void moderate(selection.selected, 'rejected'),
              })}
            >
              Rejeter la sélection
            </button>
          </BulkBar>

          {isMobile ? (
            <div className="bbm-card-stack">
              {rows.map((company) => (
                <div key={company.id} className="bbm-mobile-card">
                  <div className="bbm-mobile-card__head">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1, minWidth: 0 }}>
                      <Checkbox
                        checked={selection.isSelected(company.id)}
                        onChange={() => selection.toggle(company.id)}
                        label={`Sélectionner ${company.name}`}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div className="bbm-mobile-card__title">{company.name}</div>
                        <div className="bbm-mobile-card__sub">{company.type}</div>
                      </div>
                    </div>
                    <StatusBadge status={company.status} />
                  </div>
                  <div className="bbm-mobile-card__fields">
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Ville</span>
                      <span>{company.city}</span>
                    </div>
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Canal</span>
                      <span>{CHANNEL_LABELS[company.contactChannel] ?? company.contactChannel} · {company.contactDestination}</span>
                    </div>
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Soumis</span>
                      <span>{fmtDate(company.submittedAt)}</span>
                    </div>
                  </div>
                  <div className="bbm-mobile-card__actions">
                    {company.status === 'pending' ? (
                      <>
                        <button type="button" className="bbm-btn bbm-btn--success" onClick={() => setPending({
                          title: 'Approuver cette entreprise ?',
                          body: 'Le statut passera à Approuvée.',
                          confirmLabel: 'Approuver',
                          fn: () => void moderate([company.id], 'approved'),
                        })}>Approuver</button>
                        <button type="button" className="bbm-btn bbm-btn--danger" onClick={() => setPending({
                          title: 'Rejeter cette entreprise ?',
                          body: 'Le statut passera à Rejetée.',
                          confirmLabel: 'Rejeter',
                          danger: true,
                          fn: () => void moderate([company.id], 'rejected'),
                        })}>Rejeter</button>
                      </>
                    ) : null}
                    <button type="button" className="bbm-btn" onClick={() => setDetail(company)}>Voir</button>
                    <button type="button" className="bbm-btn" onClick={() => openEditor(company)}>Modifier</button>
                    <button type="button" className="bbm-btn bbm-btn--danger" onClick={() => setPending({
                      title: 'Supprimer cette entreprise ?',
                      body: 'Action irréversible. Toutes les offres et abonnements seront supprimés.',
                      confirmLabel: 'Supprimer',
                      danger: true,
                      fn: () => void deleteCompany(company.id),
                    })}>Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bbm-panel">
              <div className="bbm-row bbm-row--head">
                <Checkbox
                  checked={selection.allSelected(visibleIds)}
                  onChange={() => selection.toggleAll(visibleIds)}
                  label="Tout sélectionner"
                />
                <div style={{ flex: '1 1 0', minWidth: 0 }}>Entreprise</div>
                <div style={{ width: 90, flexShrink: 0 }}>Ville</div>
                <div style={{ width: 160, flexShrink: 0 }}>Canal contact</div>
                <div style={{ width: 110, flexShrink: 0 }}>Statut</div>
                <div style={{ width: 100, flexShrink: 0 }}>Soumis le</div>
                <div style={{ width: 450, flexShrink: 0, textAlign: 'right', borderLeft: '1px solid transparent', paddingLeft: 20, marginLeft: 4 }}>Actions</div>
              </div>

              {rows.map((company) => (
                <div key={company.id} className="bbm-row">
                  <Checkbox
                    checked={selection.isSelected(company.id)}
                    onChange={() => selection.toggle(company.id)}
                    label={`Sélectionner ${company.name}`}
                  />
                  <button
                    type="button"
                    onClick={() => setDetail(company)}
                    style={{
                      flex: '1 1 0',
                      minWidth: 0,
                      textAlign: 'left',
                      border: 'none',
                      background: 'none',
                      padding: 0,
                    }}
                  >
                    <div className="bbm-cell-strong bbm-truncate">{company.name}</div>
                    <div className="bbm-cell-sub">{company.type}</div>
                  </button>
                  <div className="bbm-cell" style={{ width: 90, flexShrink: 0 }}>
                    {company.city}
                  </div>
                  <div className="bbm-cell bbm-truncate" style={{ width: 160, flexShrink: 0 }}>
                    <span style={{ color: '#6B7684', fontSize: 12 }}>
                      {CHANNEL_LABELS[company.contactChannel] ?? company.contactChannel}
                    </span>
                    <br />
                    <span style={{ fontSize: 12.5 }}>{company.contactDestination}</span>
                  </div>
                  <div style={{ width: 110, flexShrink: 0 }}>
                    <StatusBadge status={company.status} />
                  </div>
                  <div className="bbm-cell" style={{ width: 100, flexShrink: 0, color: '#8A95A3' }}>
                    {fmtDate(company.submittedAt)}
                  </div>
                  <div style={{ width: 450, flexShrink: 0, display: 'flex', gap: 6, justifyContent: 'flex-end', borderLeft: '1px solid #E4E8ED', paddingLeft: 20, marginLeft: 4 }}>
                    {company.status === 'pending' ? (
                      <>
                        <button
                          type="button"
                          className="bbm-btn bbm-btn--success"
                          onClick={() => setPending({
                            title: 'Approuver cette entreprise ?',
                            body: 'Le statut passera à Approuvée.',
                            confirmLabel: 'Approuver',
                            fn: () => void moderate([company.id], 'approved'),
                          })}
                        >
                          Approuver
                        </button>
                        <button
                          type="button"
                          className="bbm-btn bbm-btn--danger"
                          onClick={() => setPending({
                            title: 'Rejeter cette entreprise ?',
                            body: 'Le statut passera à Rejetée.',
                            confirmLabel: 'Rejeter',
                            danger: true,
                            fn: () => void moderate([company.id], 'rejected'),
                          })}
                        >
                          Rejeter
                        </button>
                      </>
                    ) : null}
                    <button type="button" className="bbm-btn" onClick={() => setDetail(company)}>Voir</button>
                    <button type="button" className="bbm-btn" onClick={() => openEditor(company)}>Modifier</button>
                    <button
                      type="button"
                      className="bbm-btn bbm-btn--danger"
                      onClick={() => setPending({
                        title: 'Supprimer cette entreprise ?',
                        body: 'Action irréversible. Toutes les offres et abonnements seront supprimés.',
                        confirmLabel: 'Supprimer',
                        danger: true,
                        fn: () => void deleteCompany(company.id),
                      })}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Pager
            page={resource.data.page}
            pageCount={resource.data.pageCount}
            total={resource.data.total}
            pageSize={resource.data.pageSize}
            onChange={setPage}
          />
        </>
      ) : null}

      {detail ? (
        <Drawer title={detail.name} subtitle={detail.type} onClose={() => setDetail(null)}>
          <StatusBadge status={detail.status} />
          <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div className="bbm-detail-label">Ville</div>
              <div className="bbm-detail-value">{detail.city}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Canal de contact</div>
              <div className="bbm-detail-value">
                {CHANNEL_LABELS[detail.contactChannel] ?? detail.contactChannel}
              </div>
            </div>
            <div>
              <div className="bbm-detail-label">Destination de contact</div>
              <div className="bbm-detail-value">{detail.contactDestination}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Email propriétaire</div>
              <div className="bbm-detail-value">{detail.email || '—'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Téléphone propriétaire</div>
              <div className="bbm-detail-value">{detail.ownerPhone || '—'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Téléphone de l'entreprise</div>
              <div className="bbm-detail-value">{detail.phone ?? '—'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Soumis le</div>
              <div className="bbm-detail-value">{fmtDate(detail.submittedAt)}</div>
            </div>
            {detail.approvedAt ? (
              <div>
                <div className="bbm-detail-label">Approuvé le</div>
                <div className="bbm-detail-value">{fmtDate(detail.approvedAt)}</div>
              </div>
            ) : null}
            {detail.rejectedAt ? (
              <div>
                <div className="bbm-detail-label">Rejeté le</div>
                <div className="bbm-detail-value">{fmtDate(detail.rejectedAt)}</div>
              </div>
            ) : null}
            {detail.rejectionReason ? (
              <div>
                <div className="bbm-detail-label">Motif de rejet</div>
                <div className="bbm-detail-value" style={{ color: '#D32F2F' }}>
                  {detail.rejectionReason}
                </div>
              </div>
            ) : null}
          </div>
          {detail.status === 'pending' ? (
            <div style={{ display: 'flex', gap: 10, marginTop: 26 }}>
              <button
                type="button"
                className="bbm-btn bbm-btn--success bbm-btn--block"
                onClick={() => setPending({
                  title: 'Approuver cette entreprise ?',
                  body: 'Le statut passera à Approuvée.',
                  confirmLabel: 'Approuver',
                  fn: () => void moderate([detail.id], 'approved'),
                })}
              >
                Approuver
              </button>
              <button
                type="button"
                className="bbm-btn bbm-btn--danger bbm-btn--block"
                onClick={() => setPending({
                  title: 'Rejeter cette entreprise ?',
                  body: 'Le statut passera à Rejetée.',
                  confirmLabel: 'Rejeter',
                  danger: true,
                  fn: () => void moderate([detail.id], 'rejected'),
                })}
              >
                Rejeter
              </button>
            </div>
          ) : null}
          <button
            type="button"
            className="bbm-btn bbm-btn--block"
            style={{ marginTop: 10 }}
            onClick={() => openEditor(detail)}
          >
            Modifier les informations
          </button>
          <button
            type="button"
            className="bbm-btn bbm-btn--danger bbm-btn--block"
            style={{ marginTop: 10 }}
            onClick={() => setPending({
              title: 'Supprimer cette entreprise ?',
              body: 'Action irréversible. Toutes les offres et abonnements seront supprimés.',
              confirmLabel: 'Supprimer',
              danger: true,
              fn: () => void deleteCompany(detail.id),
            })}
          >
            Supprimer l'entreprise
          </button>
        </Drawer>
      ) : null}

      {editing ? (
        <Modal
          title="Modifier l'entreprise"
          onClose={() => setEditing(null)}
          onSubmit={() => void save()}
          busy={saving}
        >
          <TextField
            label="Nom"
            value={draft.name}
            error={fieldErrors.name}
            onChange={(value) => setDraft({ ...draft, name: value })}
          />
          <div className="bbm-field">
            <label className="bbm-label">Type</label>
            <select
              className="bbm-input"
              value={draft.type}
              onChange={(e) => setDraft({ ...draft, type: e.target.value })}
            >
              <option value="">— Sélectionner —</option>
              {typeOptions.map((t) => (
                <option key={t.id} value={t.slug}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="bbm-field">
            <label className="bbm-label">Ville</label>
            <select
              className="bbm-input"
              value={draft.city}
              onChange={(e) => setDraft({ ...draft, city: e.target.value })}
            >
              <option value="">— Sélectionner —</option>
              {cityOptions.map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="bbm-field">
            <label className="bbm-label">Canal de contact</label>
            <select
              className="bbm-input"
              value={draft.contactChannel}
              onChange={(e) => setDraft({ ...draft, contactChannel: e.target.value })}
            >
              <option value="call">Appel</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
            </select>
          </div>
          <TextField
            label="Destination de contact"
            value={draft.contactDestination}
            error={fieldErrors.contactDestination}
            onChange={(value) => setDraft({ ...draft, contactDestination: value })}
          />
          <div className="bbm-field">
            <label className="bbm-label">Téléphone de l'entreprise</label>
            <input
              type="tel"
              className="bbm-input"
              value={draft.phone ?? ''}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value || null })}
              placeholder="+22507123456"
            />
          </div>
        </Modal>
      ) : null}

      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {creating ? (
        <Modal
          title="Nouvelle entreprise"
          onClose={() => setCreating(false)}
          onSubmit={() => void createCompany()}
          busy={createSaving}
        >
          <div className="bbm-field">
            <label className="bbm-label">Propriétaire (utilisateur)</label>
            <select
              className="bbm-input"
              value={createDraft.ownerId}
              onChange={(e) => setCreateDraft({ ...createDraft, ownerId: e.target.value })}
            >
              {usersResource.loading ? (
                <option value="">Chargement…</option>
              ) : usersResource.error ? (
                <option value="">Erreur chargement utilisateurs</option>
              ) : (
                <>
                  <option value="">— Sélectionner ({userOptions.length}) —</option>
                  {userOptions.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} — {u.email || u.phone}
                    </option>
                  ))}
                </>
              )}
            </select>
            {createErrors.ownerId ? (
              <div className="bbm-field-error">{createErrors.ownerId}</div>
            ) : null}
          </div>
          <TextField
            label="Nom de l'entreprise"
            value={createDraft.name}
            error={createErrors.name}
            onChange={(value) => setCreateDraft({ ...createDraft, name: value })}
          />
          <div className="bbm-field">
            <label className="bbm-label">Type</label>
            <select
              className="bbm-input"
              value={createDraft.type}
              onChange={(e) => setCreateDraft({ ...createDraft, type: e.target.value })}
            >
              <option value="">— Sélectionner —</option>
              {typeOptions.map((t) => (
                <option key={t.id} value={t.slug}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="bbm-field">
            <label className="bbm-label">Ville</label>
            <select
              className="bbm-input"
              value={createDraft.city}
              onChange={(e) => setCreateDraft({ ...createDraft, city: e.target.value })}
            >
              <option value="">— Sélectionner —</option>
              {cityOptions.map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="bbm-field">
            <label className="bbm-label">Canal de contact</label>
            <select
              className="bbm-input"
              value={createDraft.contactChannel}
              onChange={(e) => setCreateDraft({ ...createDraft, contactChannel: e.target.value })}
            >
              <option value="call">Appel</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
            </select>
          </div>
          <TextField
            label="Destination de contact"
            value={createDraft.contactDestination}
            error={createErrors.contactDestination}
            onChange={(value) => setCreateDraft({ ...createDraft, contactDestination: value })}
          />
        </Modal>
      ) : null}
    </>
  );
}
